import React, { useEffect, useState } from 'react';
import { Clock } from 'lucide-react';
import {
  deriveLaoResult,
  formatLaoCountdown,
  tickRemainingTime,
} from '../../utils/laoUtils';

function DigitCell({ digit, tone = 'neutral' }) {
  const tones = {
    bottom:
      'border-brand-gold bg-gradient-to-b from-amber-200 to-brand-gold text-red-950',
    top: 'border-white/40 bg-white text-brand-red',
    both: 'border-brand-gold bg-gradient-to-b from-white to-amber-100 text-brand-red',
    muted: 'border-white/15 bg-white/10 text-white',
  };
  return (
    <span
      className={`flex h-14 w-11 items-center justify-center rounded-xl border-2 font-mono text-3xl font-black shadow-lg sm:h-20 sm:w-16 sm:text-5xl ${tones[tone]}`}
    >
      {digit}
    </span>
  );
}

export function LaoHero({ overview }) {
  const [displayTime, setDisplayTime] = useState(overview?.remainingTime);

  useEffect(() => {
    setDisplayTime(overview?.remainingTime);
  }, [overview?.remainingTime]);

  useEffect(() => {
    if (!displayTime || displayTime.isExpired) return undefined;
    const timer = setInterval(() => {
      setDisplayTime((prev) => tickRemainingTime(prev));
    }, 1000);
    return () => clearInterval(timer);
  }, [displayTime]);

  const last = overview?.lastResult;
  const digits = last ? deriveLaoResult(last.six).six.split('') : ['-', '-', '-', '-', '-', '-'];
  const round = overview?.round;
  const bettingOpen = overview?.bettingOpen;

  return (
    <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#2b0a12] via-brand-red to-[#7a1a1a] p-5 text-white shadow-2xl sm:p-8">
      <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-white/10 blur-2xl" />
      <div className="pointer-events-none absolute bottom-0 left-10 h-32 w-32 rounded-full border-[10px] border-white/15" />
      <div className="pointer-events-none absolute -bottom-10 right-6 h-40 w-40 rounded-full bg-brand-gold/20 blur-xl" />

      <div className="relative flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.35em] text-brand-gold-light">
            Flagship · ຫວຍພັດທະນາ
          </p>
          <h1 className="mt-2 font-sans text-3xl font-black tracking-tight sm:text-5xl">
            หวยลาวพัฒนา
          </h1>
          <p className="mt-2 max-w-xl text-sm text-white/80 sm:text-base">
            แทงแบบไทย 3 บน · โต๊ด · 2 บน-ล่าง · วิ่ง อ้างผลจากเลข 6 หลัก
            ออก {overview?.config?.scheduleLabel || 'จันทร์ พุธ ศุกร์ 20:00 น.'}
          </p>
        </div>
        <div className="rounded-2xl border border-white/15 bg-black/20 px-4 py-3 text-right backdrop-blur-sm">
          <p className="text-xs text-white/70">
            {bettingOpen ? 'ปิดรับแทงใน' : 'ออกรางวัลใน'}
          </p>
          <p className="mt-1 flex items-center justify-end gap-2 font-mono text-2xl font-bold text-brand-gold-light sm:text-3xl">
            <Clock className="h-5 w-5" />
            {formatLaoCountdown(displayTime)}
          </p>
          <p className="mt-1 text-xs text-white/70">
            งวด {round?.periodLabel || '-'} · รอบ #{round?.roundNumber || '-'}
          </p>
        </div>
      </div>

      <div className="relative mt-8">
        <p className="mb-3 text-center text-xs font-semibold uppercase tracking-[0.25em] text-brand-gold-light">
          {last ? `ผลล่าสุด งวด ${last.periodLabel}` : 'รอผลงวดแรก'}
        </p>
        <div className="flex justify-center gap-2 sm:gap-3">
          {digits.map((digit, index) => {
            const isBottom = index === 2 || index === 3;
            const isTop = index >= 3;
            let tone = 'muted';
            if (last && isBottom && isTop) tone = 'both';
            else if (last && isBottom) tone = 'bottom';
            else if (last && isTop) tone = 'top';
            return <DigitCell key={`${digit}-${index}`} digit={digit} tone={tone} />;
          })}
        </div>
        {last ? (
          <div className="mt-5 grid grid-cols-3 gap-2 sm:gap-3">
            <div className="rounded-xl bg-white/10 px-3 py-3 text-center backdrop-blur-sm">
              <p className="text-[11px] text-white/70">3 ตัวบน</p>
              <p className="font-mono text-2xl font-black text-white">{last.threeTop}</p>
            </div>
            <div className="rounded-xl bg-white/10 px-3 py-3 text-center backdrop-blur-sm">
              <p className="text-[11px] text-white/70">2 ตัวบน</p>
              <p className="font-mono text-2xl font-black text-white">{last.twoTop}</p>
            </div>
            <div className="rounded-xl bg-brand-gold/20 px-3 py-3 text-center ring-1 ring-brand-gold/50">
              <p className="text-[11px] text-brand-gold-light">2 ตัวล่าง</p>
              <p className="font-mono text-2xl font-black text-brand-gold-light">
                {last.twoBottom}
              </p>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}
